// Settings: Outlook connection, automatic reminders, new feedback emails, Team (platform-admin staff), tool catalog editor, "Other" values by frequency.
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { ChevronDown, ChevronUp, GripVertical } from 'lucide-react';
import { FUNCTIONS, isFeedbackEmailAddress, type CatalogEntry, type DeletedOnboarding, type FunctionId, type InviteStaffRequest, type InviteStaffResponse, type OtherValue, type PortalSettings, type PortalSettingsPatch, type StaffMember } from '@upm/domain';
import { api, ApiError } from '../lib/api';
import { fmtAgo, fmtDate } from '../lib/format';
import { Avatar, Banner, Button, Card, cx, Dialog, initialsOf, Pill, Select, Spinner, TableBox, Td, TextField, Th, type PillTone } from '../ui';
import { useMe } from './AdminShell';
import { usePortalSettings } from './portalSettings';

export function SettingsPage() {
  return (
    <>
      <div className="flex flex-wrap items-baseline gap-x-3 gap-y-0.5">
        <h1 className="page-title">Settings</h1>
        <p className="m-0 text-[13px] text-muted">Your Outlook connection, automatic reminders, new feedback emails, who's on the team, and the tool lists firms pick from.</p>
      </div>
      <OutlookCard />
      <RemindersCard />
      <FeedbackEmailsCard />
      <TeamCard />
      <DeletedOnboardings />
      <CatalogEditor />
      <OtherValues />
    </>
  );
}

const REMINDERS: Array<{ key: keyof PortalSettingsPatch; since: 'intakeRemindersSince' | 'feedbackQuestionRemindersSince'; label: string; hint: string; turnOn: string }> = [
  {
    key: 'intakeReminders',
    label: 'Firm intake reminders',
    hint: 'Emails the firm contact from the lead: not opened after 3 days, not finished 7 days after opening, and 5 days before the link expires. One of each per link.',
    since: 'intakeRemindersSince',
    turnOn: 'Turn on firm intake reminders? Only reminders that come due from now on are sent. Firms already past one of these points don’t get that one.',
  },
  {
    key: 'feedbackQuestionReminders',
    label: 'Feedback question reminders',
    hint: 'A “Reminder — reply needed” notification in Vertekal Portal when a client has not answered a team question for 3 business days. Weekdays at 14:00 UTC, once per question.',
    since: 'feedbackQuestionRemindersSince',
    turnOn: 'Turn on feedback question reminders? Only questions whose 3 business days run out from now on get a reminder. Questions already overdue don’t.',
  },
];

/** Settings → Automatic reminders. Both start off; the scheduled jobs read these on every run. */
export function RemindersCard() {
  const { settings: s, setSettings: setS, error: err, setError: setErr } = usePortalSettings();
  const [busy, setBusy] = useState(false);
  const toggle = async (r: (typeof REMINDERS)[number], on: boolean) => {
    if (on && !window.confirm(r.turnOn)) return;
    setBusy(true);
    setErr(null);
    try { setS(await api.patch<PortalSettings>('/api/admin/settings', { [r.key]: on })); }
    catch (e) { setErr(e instanceof ApiError ? e.message : 'Could not save the change.'); }
    finally { setBusy(false); }
  };
  return (
    <Card className="flex flex-col gap-3 sm:p-6">
      <h2 className="card-title">Automatic reminders</h2>
      <p className="m-0 mt-1 text-sm text-ink-2">Reminders the portal sends on a schedule, without anyone pressing send. While a switch is off, nothing goes out and you nudge people yourself.</p>
      {!s ? (!err && <Spinner label="Loading reminder settings" />) : (
        <>
          {REMINDERS.map((r) => (
            <label key={r.key} className="flex items-start gap-2.5 text-sm">
              <input type="checkbox" className="accent-brand mt-0.5 h-[18px] w-[18px]" checked={s[r.key]} disabled={busy} onChange={(e) => void toggle(r, e.target.checked)} />
              <span>{r.label} <Pill tone={s[r.key] ? 'ok' : 'neutral'}>{s[r.key] ? 'On' : 'Off'}</Pill>{s[r.since] && <span className="ml-1 text-[13px] text-muted">since {fmtDate(s[r.since]!)}</span>}<span className="block text-[13px] text-muted">{r.hint}</span></span>
            </label>
          ))}
          {s.updatedBy && s.updatedAt && <p className="m-0 text-[13px] text-muted">Last changed by {s.updatedBy} · {fmtAgo(s.updatedAt)}</p>}
        </>
      )}
      {err && <Banner tone="error" role="alert">{err}</Banner>}
    </Card>
  );
}

/**
 * Settings → New feedback emails. One email per new feedback request, from the system mailbox, to every vertekal.com
 * address on the list; the switch starts off. Firms never get these (the API's recipient guard allows only the list).
 */
export function FeedbackEmailsCard() {
  const { settings: s, setSettings: setS, error: err, setError: setErr } = usePortalSettings();
  const [busy, setBusy] = useState(false);
  const [add, setAdd] = useState('');
  const [addErr, setAddErr] = useState<string | null>(null);
  const save = async (call: () => Promise<PortalSettings>) => {
    setBusy(true);
    setErr(null);
    try { setS(await call()); return true; }
    catch (e) { setErr(e instanceof ApiError ? e.message : 'Could not save the change.'); return false; }
    finally { setBusy(false); }
  };
  const toggle = (on: boolean) => {
    if (on && !window.confirm('Turn on new feedback emails? Everyone on the list gets an email for each feedback request that comes in from now on.')) return;
    void save(() => api.patch<PortalSettings>('/api/admin/settings', { feedbackEmails: on }));
  };
  const submit = async () => {
    const email = add.trim().toLowerCase();
    if (!isFeedbackEmailAddress(email)) { setAddErr('Use a vertekal.com email address.'); return; }
    setAddErr(null);
    if (await save(() => api.post<PortalSettings>('/api/admin/settings/feedback-recipients', { email }))) setAdd('');
  };
  return (
    <Card className="flex flex-col gap-3 sm:p-6">
      <h2 className="card-title">New feedback emails</h2>
      <p className="m-0 mt-1 text-sm text-ink-2">One email per new feedback request, from no-reply@vertekal.com, to everyone on this list. A bug says auto-fix will triage it and post a review page with a draft PR; a feature or question says it waits for the team. Firms never get these.</p>
      {!s ? (!err && <Spinner label="Loading feedback email settings" />) : (
        <>
          <label className="flex items-start gap-2.5 text-sm">
            <input type="checkbox" className="accent-brand mt-0.5 h-[18px] w-[18px]" checked={s.feedbackEmails} disabled={busy} onChange={(e) => toggle(e.target.checked)} />
            <span>Email the team about new feedback <Pill tone={s.feedbackEmails ? 'ok' : 'neutral'}>{s.feedbackEmails ? 'On' : 'Off'}</Pill>{s.feedbackEmailsSince && <span className="ml-1 text-[13px] text-muted">since {fmtDate(s.feedbackEmailsSince)}</span>}</span>
          </label>
          {s.feedbackEmailRecipients.length > 0 ? (
            <ul aria-label="Who gets new feedback emails" className="m-0 flex list-none flex-col rounded-lg border border-line p-0">
              {s.feedbackEmailRecipients.map((r) => (
                <li key={r.email} className="flex items-center justify-between gap-3 border-b border-line-soft px-3 py-2 text-sm last:border-b-0">
                  <span className="min-w-0 break-all text-ink">{r.email}</span>
                  <button type="button" className="text-[13px] font-semibold text-brand hover:underline disabled:opacity-50" disabled={busy} aria-label={`Remove ${r.email}`}
                    onClick={() => void save(() => api.del<PortalSettings>(`/api/admin/settings/feedback-recipients/${encodeURIComponent(r.email)}`))}>Remove</button>
                </li>
              ))}
            </ul>
          ) : <p className="m-0 text-sm text-muted">No one is on the list, so no emails go out.</p>}
          <form className="flex flex-wrap items-end gap-2" onSubmit={(e) => { e.preventDefault(); void submit(); }}>
            <TextField className="min-w-[240px] flex-1" label="Add someone" hint="A vertekal.com address." placeholder="name@vertekal.com" inputMode="email" autoComplete="off"
              value={add} error={addErr} onChange={(e) => { setAdd(e.target.value); setAddErr(null); }} />
            <Button variant="secondary" type="submit" disabled={busy || !add.trim()}>Add</Button>
          </form>
          {s.updatedBy && s.updatedAt && <p className="m-0 text-[13px] text-muted">Last changed by {s.updatedBy} · {fmtAgo(s.updatedAt)}</p>}
        </>
      )}
      {err && <Banner tone="error" role="alert">{err}</Banner>}
    </Card>
  );
}

function OutlookCard() {
  const { me, reloadMe } = useMe();
  const [params] = useSearchParams();
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const connect = async () => {
    setBusy(true);
    setErr(null);
    try { const { url } = await api.post<{ url: string }>('/api/admin/graph/connect-ticket', {}); window.location.assign(url); }
    catch (e) { setErr(e instanceof ApiError ? e.message : 'Could not start the Outlook connection.'); setBusy(false); }
  };
  const disconnect = async () => {
    if (!window.confirm(`Disconnect Outlook? Invites and firm emails will go out from ${me?.systemMailbox ?? 'no-reply@vertekal.com'} with replies to you, and booking falls back to “contact me”.`)) return;
    setBusy(true);
    try { await api.del('/api/admin/graph'); await reloadMe(); } catch (e) { setErr(e instanceof ApiError ? e.message : 'Could not disconnect.'); } finally { setBusy(false); }
  };
  const g = me?.graph;
  return (
    <Card className="flex flex-col gap-3 sm:p-6">
      <h2 className="card-title">Outlook connection</h2>
      <p className="m-0 mt-1 text-sm text-ink-2">Invites and firm emails send from your mailbox, and Discovery Sessions are booked on your calendar. Without it, firm emails go out from {me?.systemMailbox ?? 'no-reply@vertekal.com'} with replies to you.</p>
      {params.get('graph') === 'connected' && <Banner tone="success" role="status">Outlook connected.</Banner>}
      {params.get('graph') === 'error' && <Banner tone="error" role="alert">The Outlook connection didn’t finish. Try again.</Banner>}
      {!me ? <Spinner label="Checking connection" /> : g?.connected ? (
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex flex-wrap items-center gap-2"><Pill tone="ok" dot>Connected</Pill><span className="text-ink-2">as {g.mailbox}</span></div>
          <Button variant="secondary" disabled={busy} onClick={() => void disconnect()}>Disconnect</Button>
        </div>
      ) : (
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div><Pill tone="bad" dot>Not connected</Pill>{g?.error && <span className="mt-1 block text-[13px] text-muted">{g.error}</span>}</div>
          <Button variant="primary" disabled={busy} onClick={() => void connect()}>Connect Outlook</Button>
        </div>
      )}
      {err && <Banner tone="error" role="alert">{err}</Banner>}
    </Card>
  );
}

const STATUS: Record<StaffMember['status'], { label: string; tone: PillTone }> = {
  active: { label: 'Active', tone: 'ok' },
  invited: { label: 'Invited', tone: 'warn' },
  disabled: { label: 'Disabled', tone: 'neutral' },
};

/** The record kept after a withdrawn onboarding is deleted from its review. Hidden until there is one. */
function DeletedOnboardings() {
  const [rows, setRows] = useState<DeletedOnboarding[] | null>(null);
  const [err, setErr] = useState<string | null>(null);
  useEffect(() => {
    let live = true;
    api.get<DeletedOnboarding[]>('/api/admin/deleted-onboardings').then((d) => { if (live) setRows(d); })
      .catch((e) => { if (live) setErr(e instanceof ApiError ? e.message : 'Could not load deleted onboardings.'); });
    return () => { live = false; };
  }, []);
  if (!err && !rows?.length) return null;
  return (
    <Card className="flex flex-col gap-3 sm:p-6">
      <div>
        <h2 className="card-title">Deleted onboardings</h2>
        <p className="m-0 mt-1 text-sm text-ink-2">Withdrawn onboardings removed from Platform, and who removed them. The latest 50.</p>
      </div>
      {err && <Banner tone="error" role="alert">{err}</Banner>}
      {rows && rows.length > 0 && (
        <TableBox minWidth={560} label="Deleted onboardings">
          <thead><tr className="border-b border-line-soft"><Th>Firm</Th><Th>Contact</Th><Th>Deleted by</Th><Th>When</Th></tr></thead>
          <tbody>
            {rows.map((d) => (
              <tr key={`${d.firmName}-${d.at}`} className="border-b border-line-soft last:border-b-0">
                <Td className="font-semibold">{d.firmName}</Td>
                <Td className="text-ink-2 [overflow-wrap:anywhere]">{d.contactEmail}</Td>
                <Td>{d.deletedBy}</Td>
                <Td className="whitespace-nowrap text-muted">{fmtDate(d.at)}</Td>
              </tr>
            ))}
          </tbody>
        </TableBox>
      )}
    </Card>
  );
}

export function TeamCard() {
  const { me } = useMe();
  const [rows, setRows] = useState<StaffMember[] | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [notice, setNotice] = useState<{ tone: 'success' | 'warn'; text: string } | null>(null);
  const [inviting, setInviting] = useState(false);
  const [resending, setResending] = useState<string | null>(null);
  const systemMailbox = me?.systemMailbox ?? 'no-reply@vertekal.com';

  const load = useCallback(async () => {
    try { setRows(await api.get<StaffMember[]>('/api/admin/staff')); setErr(null); }
    catch (e) { setErr(e instanceof ApiError ? e.message : 'Could not load the team.'); }
  }, []);
  useEffect(() => { void load(); }, [load]);

  const resend = async (m: StaffMember) => {
    setResending(m.email);
    setNotice(null);
    setErr(null);
    try {
      const r = await api.post<InviteStaffResponse>('/api/admin/staff/resend', { email: m.email });
      setNotice(r.emailed
        ? { tone: 'success', text: `New invite sent to ${m.email}. The earlier temporary password no longer works.` }
        : { tone: 'warn', text: `A new temporary password was set for ${m.email}, but the email didn't send. Try again.` });
      await load();
    } catch (e) { setErr(e instanceof ApiError ? e.message : 'Could not resend the invite.'); } finally { setResending(null); }
  };

  return (
    <Card className="flex flex-col gap-4 sm:p-6">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div className="min-w-0 flex-1">
          <h2 className="card-title">Team</h2>
          <p className="m-0 mt-1 text-sm text-ink-2">Everyone who can sign in to Platform Manager. Invites go out from {systemMailbox} with a temporary password; at first sign-in each person sets their own password and an authenticator app.</p>
        </div>
        <Button variant="primary" className="self-start whitespace-nowrap" onClick={() => { setNotice(null); setInviting(true); }}>Invite a teammate</Button>
      </div>
      {err && <Banner tone="error" role="alert">{err}</Banner>}
      {notice && <Banner tone={notice.tone} role="status">{notice.text}</Banner>}
      {!rows && !err && <Spinner label="Loading the team" />}
      {rows && rows.length === 0 && <p className="m-0 text-muted">No one yet.</p>}
      {rows && rows.length > 0 && (
        <TableBox minWidth={640} label="Team members">
          <thead><tr className="border-b border-line-soft"><Th>Name</Th><Th>Email</Th><Th>Status</Th><Th>Last seen</Th><Th><span className="sr-only">Actions</span></Th></tr></thead>
          <tbody>
            {rows.map((m) => (
              <tr key={m.email} className="border-b border-line-soft last:border-b-0">
                <Td className="align-middle">
                  <span className="flex items-center gap-2.5">
                    <Avatar initials={initialsOf(m.name || m.email)} size={30} tone="soft" />
                    <span className="whitespace-nowrap font-semibold">{m.name || '—'}</span>
                  </span>
                </Td>
                <Td className="align-middle whitespace-nowrap text-ink-2">{m.email}</Td>
                <Td className="align-middle"><Pill tone={STATUS[m.status].tone} dot>{STATUS[m.status].label}</Pill></Td>
                <Td className="align-middle whitespace-nowrap text-muted">
                  {m.lastSeenAt ? <span title={fmtDate(m.lastSeenAt)}>{fmtAgo(m.lastSeenAt)}</span> : m.status === 'invited' ? `Invited ${fmtDate(m.createdAt)}` : 'Never'}
                </Td>
                <Td className="align-middle text-right">
                  {m.status === 'invited' && (
                    <Button size="sm" variant="outline" disabled={resending === m.email} onClick={() => void resend(m)}>
                      {resending === m.email ? 'Sending…' : 'Resend invite'}
                    </Button>
                  )}
                </Td>
              </tr>
            ))}
          </tbody>
        </TableBox>
      )}
      <InviteTeammateDialog
        open={inviting}
        systemMailbox={systemMailbox}
        onClose={() => setInviting(false)}
        onInvited={async (r) => {
          setNotice(r.emailed
            ? { tone: 'success', text: `Invite sent to ${r.member.email}.` }
            : { tone: 'warn', text: `${r.member.email} was added, but the invite email didn't send. Use Resend invite.` });
          await load();
        }}
      />
    </Card>
  );
}

function InviteTeammateDialog({ open, systemMailbox, onClose, onInvited }: {
  open: boolean; systemMailbox: string; onClose: () => void; onInvited: (r: InviteStaffResponse) => Promise<void>;
}) {
  const [form, setForm] = useState<InviteStaffRequest>({ name: '', email: '' });
  const [error, setError] = useState<string | null>(null);
  const [fieldErr, setFieldErr] = useState<{ name?: string; email?: string }>({});
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState<InviteStaffResponse | null>(null);
  useEffect(() => { if (open) { setForm({ name: '', email: '' }); setError(null); setFieldErr({}); setDone(null); } }, [open]);

  const submit = async () => {
    const name = form.name.trim();
    const email = form.email.trim();
    const fe = {
      ...(name ? {} : { name: 'Enter their name.' }),
      ...(/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email) ? {} : { email: 'Enter a valid email address.' }),
    };
    setFieldErr(fe);
    if (fe.name || fe.email) return;
    setBusy(true);
    setError(null);
    try {
      const r = await api.post<InviteStaffResponse>('/api/admin/staff', { name, email });
      setDone(r);
      await onInvited(r);
    } catch (e) {
      if (e instanceof ApiError && e.issues.some((i) => i.path === 'email')) setFieldErr({ email: e.message });
      else setError(e instanceof ApiError ? e.message : 'Could not send the invite.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog
      open={open}
      title={done ? 'Invite sent' : 'Invite a teammate'}
      onClose={onClose}
      footer={done ? <Button variant="primary" onClick={onClose}>Done</Button> : (
        <>
          <Button onClick={onClose}>Cancel</Button>
          <Button variant="primary" disabled={busy} onClick={() => void submit()}>{busy ? 'Sending…' : 'Send invite'}</Button>
        </>
      )}
    >
      {done ? (
        done.emailed
          ? <Banner tone="success" role="status">{done.member.name || done.member.email} will get an email from {systemMailbox} with their sign-in email and a temporary password, valid for 7 days.</Banner>
          : <Banner tone="warn" role="alert">{done.member.email} was added to the team, but the invite email didn't send. Use Resend invite on their row.</Banner>
      ) : (
        <form className="flex flex-col gap-4" noValidate onSubmit={(e) => { e.preventDefault(); void submit(); }}>
          <p className="m-0 text-sm text-ink-2">They'll get an email from {systemMailbox} with a temporary password. At first sign-in they set their own password and an authenticator app.</p>
          <TextField label="Name" autoComplete="off" value={form.name} error={fieldErr.name} onChange={(e) => { setForm({ ...form, name: e.target.value }); setFieldErr({ ...fieldErr, name: undefined }); }} />
          <TextField label="Work email" type="email" autoComplete="off" hint="Their @vertekal.com address. It's their sign-in." value={form.email} error={fieldErr.email} onChange={(e) => { setForm({ ...form, email: e.target.value }); setFieldErr({ ...fieldErr, email: undefined }); }} />
          {error && <Banner tone="error" role="alert">{error}</Banner>}
          <button type="submit" hidden />
        </form>
      )}
    </Dialog>
  );
}

/** Tool catalog: drag (or Move up/down) to reorder; label edits save on leaving the field; Shown saves on click. */
export function CatalogEditor() {
  const [entries, setEntries] = useState<CatalogEntry[] | null>(null);
  const [fn, setFn] = useState<FunctionId>('crm');
  const [err, setErr] = useState<string | null>(null);
  const [saved, setSaved] = useState<string | null>(null);
  const [newLabel, setNewLabel] = useState('');
  const [dragging, setDragging] = useState<string | null>(null);
  const [over, setOver] = useState<string | null>(null);
  // The row being dragged, read by dragover/drop before React re-renders.
  const dragId = useRef<string | null>(null);
  const endDrag = () => { dragId.current = null; setDragging(null); setOver(null); };

  const load = useCallback(async () => {
    try { setEntries(await api.get<CatalogEntry[]>('/api/admin/catalog')); } catch (e) { setErr(e instanceof ApiError ? e.message : 'Could not load the catalog.'); }
  }, []);
  useEffect(() => { void load(); }, [load]);

  const rows = useMemo(() => (entries ?? []).filter((e) => e.functionId === fn).sort((a, b) => a.sort - b.sort), [entries, fn]);
  const replace = (fresh: CatalogEntry[]) => setEntries((xs) => [...(xs ?? []).filter((x) => x.functionId !== fn), ...fresh]);
  const update = (e: CatalogEntry, p: Partial<CatalogEntry>) =>
    setEntries((xs) => xs?.map((x) => (x.functionId === e.functionId && x.toolId === e.toolId ? { ...x, ...p } : x)) ?? null);
  const fail = (x: unknown, fallback: string) => { setSaved(null); setErr(x instanceof ApiError ? x.message : fallback); };

  const patch = async (e: CatalogEntry, p: Partial<Pick<CatalogEntry, 'label' | 'active'>>, done: string) => {
    setErr(null);
    try {
      update(e, await api.patch<CatalogEntry>(`/api/admin/catalog/${e.functionId}/${encodeURIComponent(e.toolId)}`, p));
      setSaved(done);
    } catch (x) { fail(x, 'Could not save.'); await load(); }
  };
  const saveLabel = (e: CatalogEntry, label: string) => {
    const next = label.trim();
    if (!next) { void load(); return; }               // empty: put the saved label back
    void patch(e, { label: next }, `${next} saved.`);
  };
  const reorder = async (ids: string[], done: string) => {
    const before = rows;
    replace(ids.map((id, i) => ({ ...rows.find((r) => r.toolId === id)!, sort: (i + 1) * 10 })));
    setErr(null);
    try { replace(await api.put<CatalogEntry[]>(`/api/admin/catalog/${fn}/order`, { toolIds: ids })); setSaved(done); }
    catch (x) { replace(before); fail(x, 'Could not save the order.'); }
  };
  const move = (from: number, to: number) => {
    if (to < 0 || to >= rows.length || from === to) return;
    const ids = rows.map((r) => r.toolId);
    const [id] = ids.splice(from, 1);
    ids.splice(to, 0, id!);
    void reorder(ids, `${rows[from]!.label} moved to position ${to + 1}.`);
  };
  const add = async () => {
    const label = newLabel.trim();
    if (!label) return;
    setErr(null);
    try {
      await api.post('/api/admin/catalog', { functionId: fn, label });
      setNewLabel(''); setSaved(`${label} added.`);
      await load();
    } catch (x) { fail(x, 'Could not add the tool.'); }
  };

  return (
    <Card className="flex flex-col gap-4 sm:p-6">
      <div>
        <h2 className="card-title">Tool catalog</h2>
        <p className="m-0 mt-1 text-sm text-ink-2">The options in each Step 2 dropdown, in the order firms see them. Drag to reorder. Changes save as you make them. A tool that isn’t shown stays on any firm’s answer that already picked it.</p>
      </div>
      <Select label="Business function" value={fn} onChange={(e) => setFn(e.target.value as FunctionId)} className="max-w-[360px]">
        {FUNCTIONS.map((f) => <option key={f.id} value={f.id}>{f.label}</option>)}
      </Select>
      {err && <Banner tone="error" role="alert">{err}</Banner>}
      <div aria-live="polite" className="min-h-[1.25rem] text-[13px] text-ok-fg">{saved}</div>
      {!entries ? <Spinner label="Loading catalog" /> : (
        <ol aria-label="Tools, in the order firms see them" className="m-0 flex list-none flex-col overflow-hidden rounded-card border border-line p-0">
          {rows.map((e, i) => (
            <li
              key={e.toolId}
              data-testid={`catalog-row-${e.toolId}`}
              onDragOver={(x) => { if (dragId.current) { x.preventDefault(); setOver(e.toolId); } }}
              onDrop={(x) => {
                x.preventDefault();
                const from = rows.findIndex((r) => r.toolId === dragId.current);
                endDrag();
                if (from >= 0) move(from, i);
              }}
              className={cx(
                'flex flex-wrap items-center gap-3 border-b border-line-soft bg-white px-3 py-2.5 last:border-b-0 sm:flex-nowrap',
                dragging === e.toolId && 'opacity-50',
                over === e.toolId && dragging !== e.toolId && 'shadow-[inset_0_2px_0_rgb(var(--brand))]',
              )}
            >
              <span
                draggable
                onDragStart={(x) => { dragId.current = e.toolId; setDragging(e.toolId); x.dataTransfer.effectAllowed = 'move'; x.dataTransfer.setData('text/plain', e.toolId); }}
                onDragEnd={endDrag}
                title="Drag to reorder"
                aria-hidden="true"
                className="flex h-10 w-6 flex-none cursor-grab items-center justify-center text-line-strong active:cursor-grabbing"
              >
                <GripVertical size={18} strokeWidth={2} />
              </span>
              <input
                aria-label={`Name of tool ${i + 1}`}
                className="input !h-10 min-w-0 flex-[1_1_220px]"
                value={e.label}
                onChange={(x) => update(e, { label: x.target.value })}
                onBlur={(x) => saveLabel(e, x.target.value)}
                onKeyDown={(x) => { if (x.key === 'Enter') (x.target as HTMLInputElement).blur(); }}
              />
              <label className="flex min-h-10 flex-none items-center gap-2 text-sm text-ink-2">
                <input type="checkbox" className="h-[18px] w-[18px] accent-brand" checked={e.active}
                  onChange={(x) => void patch(e, { active: x.target.checked }, `${e.label} ${x.target.checked ? 'shown' : 'hidden'}.`)} />
                Shown
              </label>
              <span className="flex flex-none gap-1">
                <Button size="sm" variant="quiet" className="!w-9 !px-0" aria-label={`Move ${e.label} up`} disabled={i === 0} onClick={() => move(i, i - 1)}><ChevronUp aria-hidden size={18} /></Button>
                <Button size="sm" variant="quiet" className="!w-9 !px-0" aria-label={`Move ${e.label} down`} disabled={i === rows.length - 1} onClick={() => move(i, i + 1)}><ChevronDown aria-hidden size={18} /></Button>
              </span>
            </li>
          ))}
        </ol>
      )}
      <form className="flex flex-wrap items-end gap-3" onSubmit={(e) => { e.preventDefault(); void add(); }}>
        <TextField label="Add a tool" hint="Goes to the bottom of this list. Using the same name as a tool elsewhere keeps them the same product." value={newLabel} onChange={(e) => setNewLabel(e.target.value)} className="min-w-[220px] flex-1" />
        <Button type="submit" variant="primary" disabled={!newLabel.trim()}>Add tool</Button>
      </form>
    </Card>
  );
}

function OtherValues() {
  const [rows, setRows] = useState<OtherValue[] | null>(null);
  const [err, setErr] = useState<string | null>(null);
  useEffect(() => {
    api.get<OtherValue[]>('/api/admin/catalog/other-values').then((r) => setRows([...r].sort((a, b) => b.count - a.count)), (e) => setErr(e instanceof ApiError ? e.message : 'Could not load.'));
  }, []);
  const label = (id: FunctionId) => FUNCTIONS.find((f) => f.id === id)?.shortLabel ?? id;
  return (
    <Card className="flex flex-col gap-3 sm:p-6">
      <div>
        <h2 className="card-title">“Other” values</h2>
        <p className="m-0 mt-1 text-sm text-ink-2">Tools firms typed in. One that keeps appearing is a candidate for the list and for a playbook.</p>
      </div>
      {err && <Banner tone="error" role="alert">{err}</Banner>}
      {!rows && !err && <Spinner label="Loading" />}
      {rows && rows.length === 0 && <p className="m-0 text-muted">None yet.</p>}
      {rows && rows.length > 0 && (
        <TableBox minWidth={480} label="Other values">
          <thead><tr className="border-b border-line-soft"><Th>Typed value</Th><Th>Business function</Th><Th>Firms</Th></tr></thead>
          <tbody>
            {rows.map((r, i) => (
              <tr key={`${r.functionId}-${r.text}-${i}`} className="border-b border-line-soft last:border-b-0">
                <Td>{r.text}</Td><Td>{label(r.functionId)}</Td><Td>{r.count}</Td>
              </tr>
            ))}
          </tbody>
        </TableBox>
      )}
    </Card>
  );
}
